// Label management for all user-facing Japanese strings.
// Organized by component / section.

export interface Labels {
  app: {
    defaultRole: string;
    defaultLoadingMessage: string;
    alertDefaultTitle: string;
    loadingCloseLabel: string;
    sessionLoadingMessage: string;
    sessionLoadError: string;
    sessionDeleteError: string;
    workflowTitle: string;
    step1Num: string;
    step1Title: string;
    step1SubOverview: string;
    step1SubPdf: string;
    step2Num: string;
    step2Title: string;
    step2SubFormula: string;
    step2SubCollect: string;
    step3Num: string;
    step3Title: string;
    step3SubRealization: string;
    step3SubMapGeneration: string;
    step4Num: string;
    step4Title: string;
    step4SubDownload: string;
    step4SubCompleted: string;
    errorTitle: string;
    localStorageRestoreError: string;
    sessionReadError: string;
  };
  header: {
    appName: string;
    navDashboard: string;
    guestName: string;
    logoutButton: string;
  };
  dashboard: {
    pageTitle: string;
    subtitle: string;
    monthlyUsageTitle: string;
    usageUnit: string;
    createMapTitle: string;
    createMapBanner: string;
    createMapDescription: string;
    prepTitle: string;
    prepOverviewLabel: string;
    prepPdfLabel: string;
    startButton: string;
    checkWarning: string;
    maxSessionsWarning: string;
    activeSessionsTitle: string;
    loadingText: string;
    noActiveSessions: string;
    sessionIdLabel: string;
    progressLabel: string;
    retryingLabel: string;
    createdAtLabel: string;
    queueWaiting: string;
    queueWaitingPosition: string;
    queueConcurrency: string;
    resumeLabel: string;
    goToExecution: string;
    deleteButton: string;
    deletingButton: string;
    deleteSessionTitle: string;
    deleteSessionConfirm: string;
    deleteSessionError: string;
    completedHistoryTitle: string;
    downloadButton: string;
    downloadPreparing: string;
    downloadError: string;
    viewButton: string;
    statusCompleted: string;
    historyLimitPrefix: string;
    historyLimitNote: string;
    historyWaitingHint: string;
    emptyHistoryWithActive: string;
    emptyHistoryHintWithActive: string;
    emptyHistoryNoActive: string;
    emptyHistoryHintNoActive: string;
    dateUnknown: string;
    actionUploadPdf: string;
    actionSelectFormula: string;
    actionSelectRealization: string;
    actionDownloadExcel: string;
    statusMap: Record<string, string>;
    jobStatusMap: Record<string, string>;
  };
  phase1: {
    stepLabel: string;
    title: string;
    description: string;
    field1Label: string;
    field1Hint: string;
    field1Placeholder: string;
    field2Label: string;
    field2Hint: string;
    field2Placeholder: string;
    field3Label: string;
    field3Hint: string;
    field3Placeholder: string;
    field4Label: string;
    field4Hint: string;
    field4Placeholder: string;
    charCountLabel: string;
    charCountUnit: string;
    errorAllRequired: string;
    errorTooShort: string;
    errorTooLong: string;
    errorGeneric: string;
    errorWorkflowStart: string;
    submitLoading: string;
    submitButton: string;
    defaultTitle: string;
    inputFieldUsage: string;
    inputFieldTech: string;
    inputFieldProblem: string;
    inputFieldEffect: string;
  };
  phase2pdf: {
    stepLabel: string;
    title: string;
    description: string;
    dropzoneText: string;
    dropzoneOr: string;
    fileSelectButton: string;
    selectedFilesTitle: string;
    noSessionWarning: string;
    noSessionAlert: string;
    uploadButton: string;
    uploadingButton: string;
    analyzingButton: string;
    uploadLoadingMessage: string;
    analyzeLoadingMessage: string;
    classifyLoadingMessage: string;
    uploadError: string;
    uploadFileTooLarge: string;
    uploadTimeout: string;
    classifyError: string;
    classifyTimeout: string;
    networkError: string;
    sessionStatusError: string;
    sessionRestoreError: string;
    jobFailedError: string;
  };
  phase2formula: {
    stepLabel: string;
    title: string;
    description: string;
    loadingMessage: string;
    selectAllButton: string;
    clearAllButton: string;
    executeButton: string;
    executingButton: string;
    emptyFormulas: string;
    formulaLabel: string;
    hitEstimate: string;
    hitOver1000: string;
    hitUnknown: string;
    hitUnit: string;
    searchExecuting: string;
    searchError: string;
    searchTimeout: string;
    searchJobStartError: string;
    generateError: string;
    generateTimeout: string;
    selectAtLeastOne: string;
    zeroFormulasAlert: string;
    searchStatusRestoreError: string;
    pdfAnalysisWaiting: string;
    formulaTypeLabels: {
      mainAndRelatedIpc: string;
      mainAndRelatedIpcFallback: string;
      mainAndKeyword: string;
      mainAndKeywordFallback: string;
      mainAndRelatedIpcAndDomain: string;
      mainAndRelatedIpcAndDomainFallback: string;
      mainAndKeywordAndDomain: string;
      mainAndKeywordAndDomainFallback: string;
    };
  };
  phase2realization: {
    stepLabel: string;
    title: string;
    description: string;
    selectionSummary: string;
    selectAllButton: string;
    clearAllButton: string;
    proceedButton: string;
    processingButton: string;
    patentCountUnit: string;
    selectAtLeastOne: string;
    extractingMessage: string;
    sessionLoadingMessage: string;
    sessionDeletedMessage: string;
    sessionMissingMessage: string;
    extractFailedMessage: string;
    analysisExecuting: string;
    componentsExtracting: string;
    qualityJudging: string;
    analysisTimeout: string;
    selectError: string;
    componentsError: string;
    qualityError: string;
    qualityNotReady: string;
    processingError: string;
    serverBusy: string;
    sessionInfoError: string;
    invalidResponseError: string;
    extractionError: string;
    extractionStartError: string;
  };
  phase3: {
    stepLabel: string;
    title: string;
    completedHeader: string;
    downloadExcelButton: string;
    downloadMapButton: string;
    finishButton: string;
    newSearchButton: string;
    retryButton: string;
    summaryTitle: string;
    totalPatents: string;
    realizationMethods: string;
    searchFormulas: string;
    referenceTitle: string;
    qualityDistributionTitle: string;
    qualityGood: string;
    qualityFair: string;
    qualityPoor: string;
    qualityOursStrong: string;
    qualityOursNormal: string;
    qualityOthersStrong: string;
    qualityOthersNormal: string;
    qualityUnit: string;
    workflowSummaryTitle: string;
    phase1InputInfo: string;
    phase1Summary1: string;
    phase1Summary2: string;
    phase1Summary3: string;
    phase2bFormulaGeneration: string;
    phase2cRealizationExtraction: string;
    pdfAnalyzedCount: string;
    formulaGeneratedCount: string;
    realizationExtractedCount: string;
    realizationSelectedSuffix: string;
    inputUsageField: string;
    inputTechFeature: string;
    inputProblem: string;
    inputEffect: string;
    loadingResultData: string;
    loadingExcelUrl: string;
    loadingQualityWaiting: string;
    excelGenNotReady: string;
    excelGenFailed: string;
    excelJobStartFailed: string;
    excelJobStatusFailed: string;
    sessionFetchFailed: string;
    resultGenFailed: string;
  };
  modals: {
    alertCloseButton: string;
    confirmDefaultTitle: string;
    confirmDeleteButton: string;
    confirmCancelButton: string;
    loadingDefaultMessage: string;
    loadingCloseButton: string;
    noticeTitle: string;
  };
  analysisDetail: {
    title: string;
  };
  common: {
    required: string;
    processing: string;
    error: string;
  };
}

const defaultLabels: Labels = {
  app: {
    defaultRole: 'ゲスト',
    defaultLoadingMessage: '特許マップを作成しています',
    alertDefaultTitle: 'お知らせ',
    loadingCloseLabel: '🏠 管理画面へ',
    sessionLoadingMessage: 'セッション情報を読み込んでいます',
    sessionLoadError: 'セッションの読み込みに失敗しました',
    sessionDeleteError: 'セッションの削除に失敗しました',
    workflowTitle: '特許マップ作成 進行状況',
    step1Num: 'Step 1',
    step1Title: '分野の指定',
    step1SubOverview: '概要を入力',
    step1SubPdf: '特許例を入力',
    step2Num: 'Step 2',
    step2Title: '特許情報の収集',
    step2SubFormula: '特許検索式を選択',
    step2SubCollect: '特許を自動収集。技術項目を抽出',
    step3Num: 'Step 3',
    step3Title: '特許マップ作成',
    step3SubRealization: '特許マップに掲載する対象技術を選択',
    step3SubMapGeneration: '特許マップ自動生成',
    step4Num: 'Step 4',
    step4Title: '特許マップ ダウンロード',
    step4SubDownload: 'ダウンロード',
    step4SubCompleted: '完了',
    errorTitle: 'エラー',
    localStorageRestoreError: 'ローカルストレージからの復元に失敗しました',
    sessionReadError: 'セッションの読み込みに失敗しました',
  },
  header: {
    appName: '特許マップ作成システム',
    navDashboard: '管理画面へ',
    guestName: 'ゲスト',
    logoutButton: 'ログアウト',
  },
  dashboard: {
    pageTitle: '特許マップ作成システム：管理画面',
    subtitle: '特許マップの作成 と 管理 を行います。',
    monthlyUsageTitle: '月間利用状況',
    usageUnit: '件',
    createMapTitle: '特許マップ作成',
    createMapBanner: 'チェック欄を入力後、下の「特許マップの作成開始」を押してください。',
    createMapDescription:
      '指定した分野（入力した分野）について、特許情報を収集して特許マップを作成します。',

    prepTitle: '準備するもの',
    prepOverviewLabel: '特許マップを作成する分野に関する情報（利用分野、技術の特徴、課題、効果）',
    prepPdfLabel: '特許マップの対象分野の特許例（PDF形式）５～10件程度',
    startButton: '特許マップの作成開始',
    checkWarning: 'チェック欄を入力してください',
    maxSessionsWarning:
      '同時に進行できるセッションは最大3件です。\n\n不要なセッションを削除してから新規作成してください。',
    activeSessionsTitle: '進行中セッション（最大3）',
    loadingText: '情報を取得中です',
    noActiveSessions: '進行中のセッションはありません',
    sessionIdLabel: 'セッションID:',
    progressLabel: '進捗:',
    retryingLabel: 'リトライ中',
    createdAtLabel: '作成開始日時:',
    queueWaiting: 'サーバー混雑中（順番待ち:',
    queueWaitingPosition: '待機中',
    queueConcurrency: '同時処理',
    resumeLabel: '▶ 実行画面へ',
    goToExecution: '▶ 実行画面へ',
    deleteButton: '🗑 削除',
    deletingButton: '削除しています',
    deleteSessionTitle: '確認',
    deleteSessionConfirm: '本当にこのセッションを削除しますか？\nこの操作は元に戻せません。',
    deleteSessionError: 'セッションの削除に失敗しました。しばらくしてから再試行してください。',
    completedHistoryTitle: '完了した特許解析',
    downloadButton: '⬇ ダウンロード',
    downloadPreparing: 'Excelファイルを準備しています',
    downloadError: 'ダウンロードに失敗しました',
    viewButton: '👁 表示',
    statusCompleted: '完成',
    historyLimitPrefix: '最新の',
    historyLimitNote: '件のみ表示しています',
    historyWaitingHint: '少し待ってから自動で更新します',
    emptyHistoryWithActive: '完了した特許解析はまだありません',
    emptyHistoryHintWithActive: '進行中セッションが完了すると、ここに履歴が表示されます',
    emptyHistoryNoActive: 'まだ特許解析の履歴がありません',
    emptyHistoryHintNoActive: '上の「特許マップの作成開始」ボタンから開始できます',
    dateUnknown: '不明',
    actionUploadPdf: 'PDFをアップロードしてください',
    actionSelectFormula: '検索式を選択してください',
    actionSelectRealization: '実現方法を選択してください',
    actionDownloadExcel: 'Excelをダウンロードしてください',
    statusMap: {
      in_progress: '進行中',
      analyzing_pdfs: '特許例を読み取り中',
      generating_formulas: '検索条件を作成中',
      executing_final_search: '特許収集中',
      extracting_realization_methods: '技術項目を整理中',
      extracting_components: '技術要素を整理中',
      judging_patent_quality: '重要度を評価中',
      generating_excel: 'Excel作成中',
      excel_ready: 'Excel生成完了（ダウンロード待ち）',
      completed: '完了',
      failed: '失敗',
      error: 'エラー',
      error_pdf_analyze: 'PDF解析エラー',
      error_generating_formulas: '検索式生成エラー',
      error_executing_final_search: '特許検索エラー',
      error_extracting_realization_methods: '実現方法抽出エラー',
      error_extracting_components: '構成要素抽出エラー',
      error_judging_patent_quality: '特許品質評価エラー',
      error_generating_excel: 'Excel生成エラー',
    },
    jobStatusMap: {
      queued: '待機中',
      running: '実行中',
      succeeded: '成功',
      failed: '失敗',
      cancelled: 'キャンセル',
    },
  },
  phase1: {
    stepLabel: 'Step1:',
    title: '分野の指定',
    description:
      '特許マップの分野を指定するため、概要（利用分野、技術的特徴、課題、効果作用）を完了してください',
    field1Label: '1. 利用分野（Application Field）',
    field1Hint: '特許マップの対象となる技術が使用されている製品分野を記入してください',
    field1Placeholder: '記入例：無人飛行機・ドローン',
    field2Label: '2. 技術的特徴（Technical Features）',
    field2Hint: '特許マップの対象となる技術について、技術の概要（特徴を含む）を記入してください',
    field2Placeholder:
      '記入例：電波を利用して対象位置との距離・相対位置関係を取得するセンシング技術\n記入例：測定情報をもとに機体の制御精度を向上させる位置補正アルゴリズム',
    field3Label: '3. 課題（Problem/Challenge）',
    field3Hint: '特許マップの対象となる技術に関して、解決しようとしている課題を記入してください',
    field3Placeholder:
      '記入例：既存の測位手法（例：GPSや光学センサ）では、環境条件によって誤差が大きく、安定した着陸が困難\n記入例：高精度な位置合わせを実現しつつ、リアルタイムで制御可能な手段が不足している',
    field4Label: '4. 効果作用（Effect/Impact）',
    field4Hint: '特許マップの対象となる技術に関して、期待する効果（得たい結果）を記入してください',
    field4Placeholder:
      '記入例：電波による高信頼な距離・位置検出により、環境の影響を受けにくい精密な着陸を実現\n記入例：着陸位置の誤差を低減し、安全性と自動化のレベルを向上',
    charCountLabel: '合計:',
    charCountUnit: '文字',
    errorAllRequired: '全ての項目を入力してください',
    errorTooShort: '各項目をより詳しく入力してください',
    errorTooLong: '全体で2000文字以内で入力してください',
    errorGeneric: 'エラーが発生しました',
    errorWorkflowStart: 'ワークフローの開始に失敗しました',
    submitLoading: '入力内容を確認しています',
    submitButton: '次へ',
    defaultTitle: '特許マップ',
    inputFieldUsage: '利用分野',
    inputFieldTech: '技術的特徴',
    inputFieldProblem: '課題',
    inputFieldEffect: '効果作用',
  },
  phase2pdf: {
    stepLabel: 'Step1:',
    title: '分野の指定',
    description:
      '特許マップの分野を指定するため、特許例の入力（PDF形式、5~10件程度）を完了してください',
    dropzoneText: 'PDFファイルをここにドラッグ&ドロップ',
    dropzoneOr: 'または',
    fileSelectButton: 'ファイルを選択',
    selectedFilesTitle: '選択されたファイル',
    noSessionWarning: '警告: セッションIDがありません。先にPhase 1を完了してください。',
    noSessionAlert: 'セッションIDがありません。先にPhase 1を完了してください。',
    uploadButton: 'アップロードして解析開始',
    uploadingButton: 'アップロードしています',
    analyzingButton: '特許例を読み取っています',
    uploadLoadingMessage: '特許例をアップロードしています',
    analyzeLoadingMessage: '特許例の内容を読み取っています',
    classifyLoadingMessage: '入力内容と特許例から、関連する技術分野を整理しています',
    uploadError: 'アップロードに失敗しました',
    uploadFileTooLarge: 'アップロードに失敗しました: ファイルサイズが大きすぎます (413)',
    uploadTimeout:
      'アップロードがタイムアウトしました。ファイルサイズが大きすぎる可能性があります。',
    classifyError: '分類処理に失敗しました',
    classifyTimeout: '分類処理がタイムアウトしました。時間をおいて再試行してください。',
    networkError: 'ネットワークエラーが発生しました。ページを再読み込みしてください。',
    sessionStatusError: 'セッション状態の取得に失敗しました。ページを再読み込みしてください。',
    sessionRestoreError: 'セッション復元に失敗しました',
    jobFailedError: 'PDF解析ジョブが失敗しました',
  },
  phase2formula: {
    stepLabel: 'Step2:',
    title: '特許情報の収集',
    description:
      '特許マップに掲載する対象の特許を収集（取得）するため、適した検索式（収集式）を選択してください',
    loadingMessage: '特許を集めるための検索条件を作成しています',
    selectAllButton: '全て選択',
    clearAllButton: '選択解除',
    executeButton: '選択した検索式で実行',
    executingButton: '特許を収集しています',
    emptyFormulas: '検索式が0件のため表示できません。',
    formulaLabel: '検索式 案',
    hitEstimate: 'ヒット見込み:',
    hitOver1000: '1000件以上',
    hitUnknown: '不明',
    hitUnit: '件',
    searchExecuting: '条件に合う特許を収集しています',
    searchError: '検索実行に失敗しました',
    searchTimeout:
      '検索実行がタイムアウトしました。処理に時間がかかりすぎています。もう一度お試しください。',
    searchJobStartError: '検索ジョブの開始に失敗しました',
    generateError: '検索式の生成に失敗しました',
    generateTimeout:
      '検索式の生成がタイムアウトしました (60分)。処理に時間がかかりすぎています。もう一度お試しください。',
    selectAtLeastOne: '検索式を1つ以上選択してください',
    zeroFormulasAlert:
      '検索式が0件でした。PDFからキーワード/IPCを抽出できなかった可能性があります（画像のみPDFでOCRが必要、またはAPIキー/上限/ネットワーク等）。PDFを変えるか、OCR対応をご検討ください。※直前のPDF解析ジョブが失敗していないか /api/jobs/{job_id} も確認してください。',
    searchStatusRestoreError: '検索状態の復元に失敗しました',
    pdfAnalysisWaiting: '入力内容と特許例から、関連する技術分野を整理しています',
    formulaTypeLabels: {
      mainAndRelatedIpc: '関連を重視する検索式',
      mainAndRelatedIpcFallback: '技術分野の関連を重視する検索式',
      mainAndKeyword: '関連を重視して絞り込む検索式',
      mainAndKeywordFallback: 'キーワード関連を重視して絞り込む検索式',
      mainAndRelatedIpcAndDomain: 'の関連を重視し製品分野にも注目する検索式',
      mainAndRelatedIpcAndDomainFallback: '技術分野の関連を重視し製品分野にも注目する検索式',
      mainAndKeywordAndDomain: 'の関連を重視し製品分野にも注目して絞り込む検索式',
      mainAndKeywordAndDomainFallback: 'キーワードの関連を重視し製品分野にも注目して絞り込む検索式',
    },
  },
  phase2realization: {
    stepLabel: 'Step3:',
    title: '特許マップ作成',
    description: '特許マップに掲載する技術項目を選定してください',
    selectionSummary: '件中',
    selectAllButton: '全て選択',
    clearAllButton: '選択解除',
    proceedButton: '特許マップの作成開始（選択した項目を軸とした構成の特許マップを作成)',
    processingButton: '特許マップを作成しています',
    patentCountUnit: '件',
    selectAtLeastOne: '実現方法を1つ以上選択してください',
    extractingMessage: '特許マップに載せる技術のまとまりを整理しています',
    sessionLoadingMessage: 'セッション情報を読み込んでいます',
    sessionDeletedMessage:
      '削除されたセッションです。管理画面から新しいセッションを開始してください。',
    sessionMissingMessage:
      'セッションが見つかりません。管理画面から開き直すか、新しいセッションを開始してください。',
    extractFailedMessage:
      '実現方法の抽出状態を確認できませんでした。しばらく待ってから再度開き直してください。',
    analysisExecuting: '特許マップに必要な情報を整理しています',
    componentsExtracting: '各技術を構成する要素を整理しています',
    qualityJudging: '特許マップ上での重要度を評価しています',
    analysisTimeout: '分析処理がタイムアウトしました (20分)。もう一度お試しください。',
    selectError: '実現方法の選択に失敗しました',
    componentsError: '構成要素抽出に失敗しました',
    qualityError: '特許品質評価に失敗しました',
    qualityNotReady: '良否判定を実行できません。前の処理が完了していない可能性があります。',
    processingError: '処理に失敗しました',
    serverBusy: 'サーバーが混雑しています。しばらく待ってから再試行してください',
    sessionInfoError: 'セッション情報の取得に失敗しました',
    invalidResponseError: 'セッション情報の取得に失敗しました',
    extractionError: '実現方法の抽出に失敗しました',
    extractionStartError: '実現方法の抽出を開始できませんでした',
  },
  phase3: {
    stepLabel: 'Step4:',
    title: '特許マップ ダウンロード',
    completedHeader: '完了',
    downloadExcelButton: '📥 Excelダウンロード',
    downloadMapButton: '作成した特許マップダウンロードする',
    finishButton: '特許マップの作成処理を終了する',
    newSearchButton: '🔄 新規検索',
    retryButton: '🔄 再試行',
    summaryTitle: '特許マップの概要',
    totalPatents: '特許マップに掲載した特許の総数',
    realizationMethods: '特許マップに掲載する対象技術を選択した数',
    searchFormulas: '特許検索式（収集式）を選択した数',
    referenceTitle: '参考情報',
    qualityDistributionTitle: '特許良否判定分布',
    qualityGood: '◎',
    qualityFair: '○',
    qualityPoor: '△ 低影響度 特許',
    qualityOursStrong: '◎ TR有力特許',
    qualityOursNormal: '〇 TR標準的特許',
    qualityOthersStrong: '◎ 他社有力特許',
    qualityOthersNormal: '〇 他社標準的特許',
    qualityUnit: '件',
    workflowSummaryTitle: 'ワークフロー実行サマリー',
    phase1InputInfo: 'Step1: 分野の指定',
    phase1Summary1: '・概要を入力',
    phase1Summary2: '・4つの項目（利用分野、技術的特徴、課題、効果作用）を記載',
    phase1Summary3: '・特許例を入力',
    phase2bFormulaGeneration: 'Step2: 特許情報の収集',
    phase2cRealizationExtraction: 'Step3: 特許マップ作成',
    pdfAnalyzedCount: '件の特許例を入力',
    formulaGeneratedCount: '件の検索式案から選択し、特許データを収集',
    realizationExtractedCount: '・特許マップに掲載する対象技術として、',
    realizationSelectedSuffix: '件の実現方法から選択',
    inputUsageField: '利用分野',
    inputTechFeature: '技術的特徴',
    inputProblem: '課題',
    inputEffect: '効果作用',
    loadingResultData: '特許マップの結果を読み込んでいます',
    loadingExcelUrl: 'Excelファイルを準備しています',
    loadingQualityWaiting: '特許マップ上での重要度評価の完了を待っています',
    excelGenNotReady: 'Excel生成の準備ができていません',
    excelGenFailed: 'Excel生成に失敗しました',
    excelJobStartFailed: 'Excelジョブの開始に失敗しました',
    excelJobStatusFailed: 'Excelジョブ状態の取得に失敗しました',
    sessionFetchFailed: 'セッション取得に失敗しました',
    resultGenFailed: '結果生成に失敗しました',
  },
  modals: {
    alertCloseButton: '閉じる',
    confirmDefaultTitle: '確認',
    confirmDeleteButton: '削除する',
    confirmCancelButton: 'キャンセル',
    loadingDefaultMessage: '特許マップを作成しています',
    loadingCloseButton: '表示を閉じる',
    noticeTitle: 'お知らせ',
  },
  analysisDetail: {
    title: '特許解析詳細:',
  },
  common: {
    required: '*',
    processing: '処理を進めています',
    error: 'エラー',
  },
};

export default defaultLabels;
